import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Cookies from 'universal-cookie';
import moment from 'moment';

import history from '../../../history';
import { fetchEvents, setLastEvent } from '../../../actions';
import { BellIcon, ClipboardListIcon, UserIcon, DocumentIcon, BankIcon, XIcon } from '../ui/icons';
import EmptyState from '../ui/EmptyState';

// Redesign of newadmin/elements/Events.js. The data behavior is an exact
// port: fetchEvents on mount + 5s poll, unread dot from state.events.last_event
// vs the "last_event" cookie, and setLastEvent(moment()) on open.
const EVENT_STYLES = {
    fact_find: { icon: ClipboardListIcon, backgroundColor: '#D1FAE5', color: '#065F46' },
    voi: { icon: UserIcon, backgroundColor: '#FEF3C7', color: '#92400E' },
    document_collection: { icon: DocumentIcon, backgroundColor: '#DBEAFE', color: '#1E40AF' },
    bank_statements: { icon: BankIcon, backgroundColor: '#FEE2E2', color: '#991B1B' },
};
const DEFAULT_EVENT_STYLE = { icon: XIcon, backgroundColor: '#FEE2E2', color: '#991B1B' };

const NotificationsBell = () => {
    const events = useSelector((s) => s.events);
    const auth = useSelector((s) => s.auth);
    const dispatch = useDispatch();
    const [show, setShow] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const getEvents = () => {
            if (auth != null) dispatch(fetchEvents());
        };
        getEvents();
        const interval = setInterval(getEvents, 5000);
        return () => clearInterval(interval);
    }, [auth, dispatch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ref.current && !ref.current.contains(event.target)) {
                setShow(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const hasUnread = () => {
        if (!events?.events || events.events.length === 0) return false;
        const event = events.events[events.events.length - 1];
        let lastEvent = events.last_event;
        if (lastEvent == null) {
            const cookies = new Cookies();
            const lastEventCookie = cookies.get('last_event');
            if (lastEventCookie != null) lastEvent = lastEventCookie;
        }
        if (event != null && lastEvent != null) {
            return moment(event.date).isAfter(moment(lastEvent));
        }
        return lastEvent == null;
    };

    const toggle = () => {
        if (show === false && events.events.length > 0) {
            dispatch(setLastEvent(moment()));
        }
        setShow(!show);
    };

    const openApplication = (event) => {
        history.push('/admin/application/' + event.application_id);
        setShow(false);
    };

    const list = [...(events?.events || [])].reverse();

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={toggle}
                aria-label="Notifications"
                className="relative rounded-xl p-2.5 text-[#6B7280] hover:bg-[#F5F6FA] hover:text-[#1F2937] transition-colors"
            >
                <BellIcon className="h-5 w-5" />
                {hasUnread() ? (
                    <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-[#DC2626] ring-2 ring-white" />
                ) : null}
            </button>

            {show ? (
                <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] sm:w-96 max-h-[70vh] overflow-y-auto rounded-2xl bg-white shadow-xl border border-[#E5E7EB] z-30">
                    <div className="px-4 py-3 border-b border-[#F0F1F5]">
                        <h3 className="text-sm font-bold text-[#1F2937]">Notifications</h3>
                    </div>
                    {list.length === 0 ? (
                        <EmptyState
                            icon={<BellIcon className="h-6 w-6" />}
                            title="No notifications yet"
                            subtitle="Client activity will show up here."
                            className="py-8"
                        />
                    ) : (
                        <ul className="divide-y divide-[#F0F1F5]">
                            {list.map((event, idx) => {
                                const style = EVENT_STYLES[event.type] || DEFAULT_EVENT_STYLE;
                                const Icon = style.icon;
                                return (
                                    <li key={`${event.application_id}-${event.date}-${idx}`}>
                                        <button
                                            type="button"
                                            onClick={() => openApplication(event)}
                                            className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[#F5F6FA] transition-colors"
                                        >
                                            <span
                                                className="inline-flex items-center justify-center h-9 w-9 rounded-xl flex-shrink-0 mt-0.5"
                                                style={{ backgroundColor: style.backgroundColor, color: style.color }}
                                            >
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-sm text-[#1F2937]">
                                                    <strong>{event.name}</strong> just {event.event}
                                                </span>
                                                <span className="block text-xs font-semibold mt-0.5" style={{ color: style.color }}>
                                                    {moment(event.date).fromNow()}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default NotificationsBell;
